/**
 * 訂單 API 客戶端
 */

import { apiClient } from './client'
import type { AIParseResponse } from '@/types'

export interface PricingDetail {
  total_amount: number
  driver_amount: number
  base_fare: number
  night_surcharge: number
  cross_district_fee: number
  driver_fee?: number
  vendor_rebate?: number
  company_rebate?: number
  is_tax_inclusive?: boolean
  name_board_fee?: number
  child_seat_fee?: number
  booster_seat_fee?: number
}

export interface AdditionalStop {
  address: string
  order: number
  name?: string
  phone?: string
}

export interface Order {
  id: string
  order_number: string
  tenant_id: string
  order_category: string
  status: string
  passenger_name: string
  passenger_phone: string
  passenger_count: number
  pickup_datetime: string
  pickup_location: string
  dropoff_location: string
  additional_stops?: AdditionalStop[]
  is_round_trip: boolean
  flight_number?: string
  departure_time?: string
  arrival_time?: string
  arrival_terminal?: string
  vehicle_type: string
  luggage_count: number
  child_seat: number
  booster_seat?: number
  name_board: boolean
  is_signed: boolean
  signed_at?: string
  pricing: PricingDetail
  payment_method: string
  driver_id?: string
  driver_name?: string
  driver_phone?: string
  vehicle_number?: string
  driver_notes?: string
  edited_by_customer?: boolean
  edit_history?: string
  customer_id?: string
  customer_name?: string
  source?: 'admin' | 'customer_booking'
  // 司機操作時間記錄
  departed_at?: string
  picked_up_at?: string
  completed_at?: string
  vendor_name?: string
  vendor_paid_at?: string
  driver_paid_at?: string
  linked_order_id?: string
  external_vehicle_type?: string
  wait_minutes?: number | null
  extra_amount?: number | null
  // 簽名圖片
  signature_image_path?: string
  custom_sign_image_path?: string
  dispatch_document_path?: string
  rental_document_path?: string
  documents_generated_at?: string
  dispatch_template_key?: string
  created_at: string
  updated_at: string
}

export interface OrderCreateData {
  order_category: string
  passenger_name: string
  passenger_phone: string
  passenger_count: number
  pickup_datetime: string
  pickup_location: string
  dropoff_location: string
  additional_stops?: AdditionalStop[]
  is_round_trip: boolean
  flight_number?: string
  departure_time?: string
  arrival_time?: string
  arrival_terminal?: string
  vehicle_type: string
  luggage_count: number
  child_seat: number
  name_board: boolean
  is_signed: boolean
  pricing: PricingDetail
  payment_method: string
  driver_notes?: string
  customer_id?: string | null
  driver_id?: string
  driver_name?: string
  driver_phone?: string
  vehicle_number?: string
  vendor_name?: string
  vendor_paid_at?: string
  dispatch_template_key?: string
  wait_minutes?: number | null
  extra_amount?: number | null
}

export interface OrderUpdateData extends Partial<OrderCreateData> {
  driver_id?: string
  driver_name?: string
  driver_phone?: string
  vehicle_number?: string
  status?: string
  // 司機操作時間記錄
  departed_at?: string
  picked_up_at?: string
  completed_at?: string
  vendor_name?: string
  vendor_paid_at?: string
  driver_paid_at?: string | null
  linked_order_id?: string | null
}

export interface OrderListResponse {
  success: boolean
  data: Order[]
  total: number
  page: number
  limit: number
  total_pages: number
}

/**
 * 獲取訂單列表
 */
export const getOrders = async (params?: {
  page?: number
  limit?: number
  status?: string
  category?: string
  search?: string
  driver_id?: string
  customer_id?: string
  date_from?: string
  date_to?: string
}): Promise<OrderListResponse> => {
  const response = await apiClient.get<OrderListResponse>('/orders', { params })
  return response.data
}

/**
 * 查詢曾輸入過的乘客姓名/電話（依租戶隔離，供建立派車單時快速帶入）
 */
export interface PassengerSuggestion {
  passenger_name: string
  passenger_phone: string | null
}

export const getPassengerSuggestions = async (q: string): Promise<PassengerSuggestion[]> => {
  const response = await apiClient.get<PassengerSuggestion[]>('/orders/passenger-suggestions', { params: { q } })
  return response.data
}

/**
 * 獲取訂單詳情
 */
export const getOrder = async (id: string): Promise<Order> => {
  const response = await apiClient.get<Order>(`/orders/${id}`)
  return response.data
}

/**
 * 建立訂單
 */
export const createOrder = async (data: OrderCreateData): Promise<Order> => {
  console.log('createOrder API call with data:', data)
  const response = await apiClient.post<Order>('/orders', data)
  console.log('createOrder API response:', response.data)
  return response.data
}

/**
 * 更新訂單
 */
export const updateOrder = async (id: string, data: OrderUpdateData): Promise<Order> => {
  const response = await apiClient.put<Order>(`/orders/${id}`, data)
  return response.data
}

/**
 * 刪除訂單
 */
export const deleteOrder = async (id: string): Promise<void> => {
  await apiClient.delete(`/orders/${id}`)
}

/**
 * 使用 AI 解析訂單
 */
export const parseOrders = async (text: string): Promise<AIParseResponse> => {
  const response = await apiClient.post<AIParseResponse>('/orders/parse-ai', { text })
  return response.data
}

/**
 * 上傳圖片使用 AI 解析訂單
 */
export const parseOrderFromImage = async (file: File): Promise<AIParseResponse> => {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiClient.post<AIParseResponse>('/orders/parse-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return response.data
}

/**
 * 生成派車單圖文件
 */
/** 產生（或取回）外派免建檔司機簽單連結代碼 */
export const createExternalSignLink = async (orderId: string): Promise<string> => {
  const response = await apiClient.post<{ token: string }>(`/orders/${orderId}/external-sign-link`)
  return response.data.token
}

export const generateDispatchDocument = async (orderId: string, forceAmount = false): Promise<Blob> => {
  const response = await apiClient.post(
    `/orders/${orderId}/generate-dispatch-document`,
    {},
    { responseType: 'blob', params: forceAmount ? { force_amount: true } : undefined }
  )
  return response.data
}

/**
 * 生成出租單圖文件
 */
export const generateRentalDocument = async (orderId: string): Promise<Blob> => {
  const response = await apiClient.post(
    `/orders/${orderId}/generate-rental-document`,
    {},
    { responseType: 'blob' }
  )
  return response.data
}

/**
 * 下載舉牌檔（填入乘客資料）
 */
export const downloadSignBoard = async (orderId: string, passengerName: string): Promise<void> => {
  const response = await apiClient.get(`/orders/${orderId}/sign-board`, { responseType: 'blob' })
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const a = document.createElement('a')
  a.href = url
  a.download = `舉牌_${passengerName || orderId}.docx`
  a.click()
  window.URL.revokeObjectURL(url)
}

/**
 * 上傳簽名圖片
 */
export const uploadSignature = async (orderId: string, signatureDataUrl: string): Promise<void> => {
  // Convert data URL to blob
  const base64Data = signatureDataUrl.split(',')[1]
  const byteCharacters = atob(base64Data)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: 'image/png' })

  const formData = new FormData()
  formData.append('file', blob, `signature_${orderId}.png`)

  await apiClient.post(`/orders/${orderId}/upload-signature`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const uploadCustomSignImage = async (orderId: string, file: File): Promise<void> => {
  const formData = new FormData()
  formData.append('file', file, file.name)
  await apiClient.post(`/orders/${orderId}/upload-custom-sign-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
